import { TextNode } from 'lexical';

/**
 * A TextNode subclass that carries an explicit inline color (`__color`, a
 * #rgb/#rrggbb string). Used for the `[#hex|text]` wire markup.
 *
 * Why a subclass and not $patchStyleText: the serializer reads the color from a
 * structured field (getColor()) rather than parsing CSS, and — crucially — the
 * node keeps its bold/italic/underline format flags, so a colored-bold run stays
 * a SINGLE node and serializes cleanly to `[#hex|**x**]`. $patchStyleText tends
 * to split color and format onto adjacent nodes, which round-trips to garbage.
 */
export class ColorTextNode extends TextNode {
  static getType() {
    return 'color-text';
  }

  static clone(node) {
    return new ColorTextNode(node.__text, node.__color, node.__key);
  }

  constructor(text, color, key) {
    super(text, key);
    this.__color = color || null;
  }

  getColor() {
    return this.getLatest().__color;
  }

  setColor(color) {
    const self = this.getWritable();
    self.__color = color || null;
    return self;
  }

  createDOM(config) {
    const dom = super.createDOM(config);
    if (this.__color) dom.style.color = this.__color;
    return dom;
  }

  updateDOM(prevNode, dom, config) {
    const updated = super.updateDOM(prevNode, dom, config);
    if (prevNode.__color !== this.__color) {
      dom.style.color = this.__color || '';
    }
    return updated;
  }

  static importJSON(serializedNode) {
    const node = $createColorTextNode(serializedNode.text, serializedNode.color);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'color-text',
      color: this.__color,
      version: 1,
    };
  }
}

export function $createColorTextNode(text, color) {
  return new ColorTextNode(text, color);
}

export function $isColorTextNode(node) {
  return node instanceof ColorTextNode;
}
